import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  permission_level: string;
  is_active: number;
}

interface UserFormProps {
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PERMISSION_OPTIONS = [
  { value: "BASIC", label: "Basic Authority" },
  { value: "FULL_ACCESS", label: "Full Authority" },
  { value: "WIZARD", label: "Wizard Authority" },
  { value: "VIEW_ONLY", label: "View Only" },
  { value: "AGENCY_ADMIN", label: "Agency Admin" },
  { value: "SYSTEM_ADMIN", label: "System Administrator" },
  { value: "CUSTOM", label: "Custom" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UserForm({ user, onClose, onSuccess }: UserFormProps) {
  const isEditing = !!user;

  const [email, setEmail] = useState(user?.email ?? "");
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [password, setPassword] = useState("");
  const [permissionLevel, setPermissionLevel] = useState(
    user?.permission_level ?? "BASIC",
  );

  const createMutation = useMutation({
    mutationFn: (data: {
      email: string;
      firstName: string;
      lastName: string;
      password: string;
      permissionLevel: string;
    }) => api.post("/admin/users", data),
    onSuccess,
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      permissionLevel?: string;
    }) => api.put(`/admin/users/${user!.id}`, data),
    onSuccess,
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isEditing) {
      updateMutation.mutate({
        firstName,
        lastName,
        email,
        permissionLevel,
      });
    } else {
      createMutation.mutate({
        email,
        firstName,
        lastName,
        password,
        permissionLevel,
      });
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error ?? updateMutation.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {isEditing ? "Edit User" : "Create New User"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="First name"
            />
            <Input
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              placeholder="Last name"
            />
          </div>

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="user@agency.org"
          />

          {!isEditing && (
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Minimum 12 characters"
              minLength={12}
            />
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Permission Level
            </label>
            <select
              value={permissionLevel}
              onChange={(e) => setPermissionLevel(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {PERMISSION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {(error as Error)?.message ?? "An error occurred"}
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" loading={isPending}>
              {isEditing ? "Save Changes" : "Create User"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
